/*======================================================================================================================
  * Bluebell: src/app/state/assembly-libraries/assembly-library.effects.ts
  * Last Edited: 18/02/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import { AssemblyLibraryActions } from '@state/assembly-libraries/assembly-library.actions';
import {
  AssemblyFull,
  AssemblyLibrary,
  AssemblyLibraryCreatePartial,
  AssemblyPartial,
} from '@state/assembly-libraries/assembly-library.interfaces';
import {
  selectAssemblyLibrariesInactiveLibrariesLoaded,
  selectAssemblyLibrariesLoadInactive,
} from '@state/assembly-libraries/assembly-library.selectors';
import { selectCurrentProgramme } from '@state/programmes/programme.selectors';
import { concatMap, map, mergeMap, switchMap } from 'rxjs/operators';


@Injectable()
export class AssemblyLibraryEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private api = inject(ApiService);
  private handler = inject(EffectHandlerService);


  loadInactive = this.store.selectSignal(selectAssemblyLibrariesLoadInactive);

  create$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.create),
    concatMap((partial: AssemblyLibraryCreatePartial) => this.api.createAssemblyLibrary(partial).pipe(
      this.handler.effectResponse({
        success: (library: AssemblyLibrary) => AssemblyLibraryActions.createSuccess(library),
        successMessage: `Assembly library created`,
        retry: AssemblyLibraryActions.create(partial),
        failure: AssemblyLibraryActions.createFailure(),
        failureMessage: 'Assembly library failed to create',
        showErrorDetail: true
      })
    ))
  ));

  retrieve$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.retrieve),
    concatLatestFrom(() => this.store.select(selectCurrentProgramme)),
    switchMap(([, programme]) => this.api.listAssemblyLibraries(!this.loadInactive(), programme?.uuid).pipe(
      this.handler.effectResponse({
        success: (libraries: AssemblyLibrary[]) => AssemblyLibraryActions.retrieveSuccess(libraries),
        retry: AssemblyLibraryActions.retrieve(),
        failure: AssemblyLibraryActions.retrieveFailure(),
        failureMessage: 'Failed to retrieve assembly libraries',
        showErrorDetail: true,
      }),
    ))
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.update),
    concatMap((partial) => this.api.updateAssemblyLibrary(partial).pipe(
      this.handler.effectResponse({
        success: (library: AssemblyLibrary) => AssemblyLibraryActions.updateSuccess(library),
        successMessage: `Assembly library updated`,
        retry: AssemblyLibraryActions.update(partial),
        failure: AssemblyLibraryActions.updateFailure(),
        failureMessage: 'Failed to update assembly library',
        showErrorDetail: true
      })
    ))
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.delete),
    mergeMap(({ uuid }) => this.api.deleteAssemblyLibrary(uuid).pipe(
      this.handler.effectResponse({
        success: () => AssemblyLibraryActions.deleteSuccess(uuid),
        successMessage: 'Assembly library deleted',
        retry: AssemblyLibraryActions.delete(uuid),
        failure: AssemblyLibraryActions.deleteFailure(),
        failureMessage: 'Delete assembly library failed',
        showErrorDetail: true
      })
    ))
  ));

  toggleLoadInactive$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.toggleLoadInactive),
    concatLatestFrom(() => [
      this.store.select(selectAssemblyLibrariesLoadInactive),
      this.store.select(selectAssemblyLibrariesInactiveLibrariesLoaded),
    ]),
    map(([, loadInactive, inactiveLoaded]) => {
      if (loadInactive && !inactiveLoaded) {
        this.store.dispatch(AssemblyLibraryActions.retrieve());
      }
    })
  ), { dispatch: false });


  /*
    Assembly Actions
  */

  createAssembly$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.createAssembly),
    mergeMap((data) => this.api.createAssembly(data).pipe(
      this.handler.effectResponse({
        success: (assembly: AssemblyPartial) => AssemblyLibraryActions.createAssemblySuccess(assembly),
        successMessage: 'Assembly created',
        retry: AssemblyLibraryActions.createAssembly(data),
        failure: AssemblyLibraryActions.createAssemblyFailure(),
        failureMessage: 'Creation of assembly failed',
      })
    ))
  ));

  cloneAssembly$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.cloneAssembly),
    mergeMap((data) => this.api.cloneAssembly(data).pipe(
      this.handler.effectResponse({
        success: (assembly: AssemblyPartial) => AssemblyLibraryActions.cloneAssemblySuccess(assembly),
        retry: AssemblyLibraryActions.cloneAssembly(data),
        failure: AssemblyLibraryActions.cloneAssemblyFailure(),
      })
    ))
  ));

  selectAssembly$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.selectAssembly),
    switchMap(({ uuid }) => this.api.retrieveAssembly(uuid).pipe(
      this.handler.effectResponse({
        success: (selectedAssembly: AssemblyFull) => AssemblyLibraryActions.selectAssemblySuccess(selectedAssembly),
        retry: AssemblyLibraryActions.selectAssembly(uuid),
        failure: AssemblyLibraryActions.selectAssemblyFailure(),
      })
    ))
  ));

  updateAssembly$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.updateAssembly),
    switchMap(({ assembly, currentLib }) => this.api.updateAssembly(assembly).pipe(
      this.handler.effectResponse({
        success: (selectedAssembly: AssemblyFull) =>
          AssemblyLibraryActions.updateAssemblySuccess(selectedAssembly, currentLib ?? null),
        successMessage: 'Assembly updated',
        retry: AssemblyLibraryActions.updateAssembly({ assembly }),
        failure: AssemblyLibraryActions.updateAssemblyFailure(),
        failureMessage: 'Update assembly failed',
      })
    ))
  ));

  updateAssemblyParts$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.updateAssemblyParts),
    switchMap(({ uuid, parts }) => this.api.assemblySetParts(uuid, parts).pipe(
      this.handler.effectResponse({
        success: (selectedAssembly: AssemblyFull) => AssemblyLibraryActions.updateAssemblyPartsSuccess({ selectedAssembly }),
        successMessage: 'Assembly Parts updated',
        retry: AssemblyLibraryActions.updateAssemblyParts({ uuid, parts }),
        failure: AssemblyLibraryActions.updateAssemblyPartsFailure(),
        failureMessage: 'Updating Assembly Parts failed',
      })
    ))
  ));

  deleteAssembly$ = createEffect(() => this.actions$.pipe(
    ofType(AssemblyLibraryActions.deleteAssembly),
    mergeMap(({ uuid }) => this.api.deleteAssembly(uuid).pipe(
      this.handler.effectResponse({
        success: () => AssemblyLibraryActions.deleteAssemblySuccess(uuid),
        successMessage: 'Assembly deleted',
        retry: AssemblyLibraryActions.deleteAssembly(uuid),
        failure: AssemblyLibraryActions.deleteAssemblyFailure(),
        failureMessage: 'Delete assembly failed',
      })
    ))
  ));
}
